// NPM
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNil, forEach, find, isEmpty, map, filter, isArray, groupBy, uniqBy, cloneDeep, findIndex } from 'lodash';

// ANT COMPONENT
import { Space, Table, Row, Col, Checkbox, Alert } from 'antd';
import {
  Button,
  SkeletonLoader,
  Loader,
  ActionMenu,
  DataTable,
  CheckboxGroup,
} from '@life-line-lab/components-react';
import Icon, {
  PlusCircleOutlined,
  MinusCircleOutlined,
  ArrowRightOutlined, DeleteOutlined
} from '@ant-design/icons';

// HOOK
import useSession from "Hooks/session.hook";

// HELPER
import { toaster } from 'Helpers/index';
import { encode } from "Helpers/enc";

// STYLE
import './product-cart.scss';
import '../product.scss';

// ATOM
import { LocalCartAtom } from 'Recoil/order.atom'
import { useRecoilState } from 'recoil';
import { PlatformVoucherAtom, ProductVoucherAtom } from 'Recoil/voucher.atom';
import VacationInfoBox from 'Components/shared/vacation-info-box/vacation-info-box';
import { RiTruckLine } from 'react-icons/ri';

interface ProductCartProps {
  cartList: any;
  handleUpdateToCart: ({ }: any) => void;
  status: any;
}

const ProductCart: React.FC<ProductCartProps> = ({ status, cartList, handleUpdateToCart }) => {
  const { t } = useTranslation()
  const history = useHistory();
  const [localCartState, setLocalCartState] = useRecoilState<any>(LocalCartAtom);

  const initialInput = {
    totalAmount: 0.00
  }

  const [input, setInput] = useState<any>(initialInput);
  const [cartData, setCartData] = useState<any>([]);
  const [groupedList, setGroupedList] = useState<any>([]);
  const [selectedCartItems, setSelectedCartItems] = useState<any>([]);
  const [appliedProductVoucher, setAppliedProductVoucher] = useRecoilState<any>(ProductVoucherAtom);
  const [appliedPlatformVoucher, setAppliedPlatformVoucher] = useRecoilState<any>(PlatformVoucherAtom);

  //const savedCartData = JSON.parse(localStorage.getItem('cartData') || '[]');

  const rowSelection = {

    onSelectAll: (selected, selectedRowKeys: any, selectedRows: any) => {
      let cartItems = cloneDeep(cartData);
      if (selected == true) {
        forEach(selectedRows, rowObj => {
          rowObj.selected = true;
          return rowObj;
        })
      } else {
        forEach(selectedRows, rowObj => {
          rowObj.selected = false;
          return rowObj;
        })
      }
      let selectedItems = filter(uniqBy([...selectedRows, ...cartItems], 'cartID'), { selected: true })
      setSelectedCartItems(selectedItems)
    },
    onSelect: (record, selected, selectedRows: any) => {
      let cartItems = cloneDeep(cartData);
      record.selected = selected;
      cartItems.unshift(record);
      let selectedItems = filter(uniqBy(cartItems, 'cartID'), { selected: true })
      setSelectedCartItems(selectedItems)

    },
    selectedRowKeys: map(cartData, cartObj => { if (cartObj.selected) { return cartObj.cartID; } }),
    getCheckboxProps: (record: any) => {
      const checkoutDisabled = record.productData?.orgData?.ecomSetting?.vacationMode &&
        record.productData?.orgData?.ecomSetting?.vacationOrderHandling === "CHECKOUT_NOT_ALLOWED"

      return {
        disabled: checkoutDisabled,
      }
    }
  };

  // useEffect(() => {
  //   if (!isEmpty(savedCartData)) {
  //     setCartData(savedCartData)
  //   }
  // }, [])


  useEffect(() => {
    if (!isEmpty(cartList.items)) {
      let updatedCartItems = map(cloneDeep(cartList.items), itemObj => {
        let availableIndex = findIndex(selectedCartItems, (selected: any) => { return selected.cartID == itemObj.cartID })
        availableIndex >= 0 ? itemObj.selected = true : itemObj.selected = false;
        return itemObj;
      })
      setCartData(updatedCartItems)
    }
  }, [cartList.items])

  useEffect(() => {
    let totalAmount = 0;
    forEach(selectedCartItems, obj => {
      totalAmount += (parseFloat(obj.selectedVariant.value) * parseFloat(obj.quantity))
    })
    setInput({ ...input, totalAmount: totalAmount })
  }, [selectedCartItems])

  useEffect(() => {
    if (!isNil(cartData) && isArray(cartData)) {
      let supplierGroup = groupBy(cartData, cartObj => {
        return cartObj.productData.orgID;
      })
      let groupedCart = map(supplierGroup, (data) => {
        return {
          supplierData: data[0]?.productData?.orgData,
          products: data
        }
      })
      setGroupedList(groupedCart);
    }
  }, [cartData])

  const onProductClick = (inventory) => {
    const inventoryID = encode(inventory.inventoryID);
    history.push('/products/' + inventoryID);
  }

  /**
   * Set the input.quantity to the last input quantity that is a valid number when the input leave focus and is left empty. See Shopee.
   */
   const handleQuantityBlur = (cart: any, currentValue: any,) => {
    if (currentValue === '') {
      let lastSetQuantity;

      let cartListInfo = map(cloneDeep(cartData), cartObj => {
        if (cartObj.cartID == cart.cartID) {
          lastSetQuantity = cartObj.__lastSetQuantity;
          cartObj.quantity = lastSetQuantity;
        }
        return cartObj;
      })

      setCartData(cartListInfo);

      if (selectedCartItems.length) {
        let selectedItems = map(cloneDeep(selectedCartItems), cartObj => {
          if (cartObj.cartID == cart.cartID) {
            cartObj.quantity = lastSetQuantity;
          }
          return cartObj;
        })
        setSelectedCartItems(selectedItems);
      }

      submitUpdateCart(cartListInfo);
    }
  }

  const handleQuantityChange = (cart: any, value: string) => {
    if (status.update == 'loading') {
      return;
    }
    // handle non-digit, negative (minus sign), etc.
    value = value.replace(/\D/g, '');

    let numValue = parseInt(value);
    if (numValue === 0) return;

    // check whether exceed restricted quantity limit
    if (numValue &&
      cart?.selectedVariant?.restrictedQtyLimit &&
      numValue > parseInt(cart?.selectedVariant?.restrictedQtyLimit)
    ) {
      toaster('warning', t('error.overRestrictedLimit', { amount: parseInt(cart.selectedVariant?.restrictedQtyLimit) }));
      // set to upper bound of RESTRICTED QUANTITY if exceeded
      numValue = parseInt(cart.selectedVariant?.restrictedQtyLimit);
    }

    // check whether exceed stock limit
    if (numValue && 
      cart?.selectedVariant?.stockLimit && 
      parseInt(cart?.selectedVariant?.stockLimit) < numValue
    ) {
      toaster("warning", t('toaster.selectedQuantityError'));
      // set to upper bound of STOCK LIMIT if exceeded
      numValue = parseInt(cart.selectedVariant?.stockLimit)
    }

    let cartListInfo = map(cloneDeep(cartData), cartObj => {
      if (cartObj.cartID == cart.cartID) {
        if (value === '' && cartObj.quantity) {
          cartObj.__lastSetQuantity = cartObj.quantity; // set the last set quantity that is valid for use as fallback in calculation
        }
        cartObj.quantity = numValue || '';
      }
      return cartObj;
    })

    setCartData(cartListInfo);

    if (selectedCartItems.length) {
      let selectedItems = map(cloneDeep(selectedCartItems), cartObj => {
        if (cartObj.cartID == cart.cartID) {
          cartObj.quantity = numValue || cartObj.quantity;  // don't change the selected quantity if is empty
        }
        return cartObj;
      })
      setSelectedCartItems(selectedItems);
    }

    if (value !== '') {
      submitUpdateCart(cartListInfo)
    }
    // else, onBlur event will trigger the handleQuantityBlur() handler function,
    // which will check if the current value is ''
    // which will then set the quantity back to last value that is not ''
    // and then call submitUpdateCart() to call the backend API
  }

  const columns = [
    {
      title: t('label.product'),
      dataIndex: ['productData', 'selectedVariant'],
      width: '50%',
      render: (_: any, record: any) => {
        return (
          <div style={{ display: 'flex' }}>
            <div className="img-border">
              <img
                alt={t('alt.logo')}
                // src={find(record.productData.productImages, (productImg) => {
                //   return productImg.variantCode === record.selectedVariant.variantCode
                // })?.image || '/assets/images/product-placeholder.png'}
                // src={record.productData.featuredImage?.image || '/assets/images/product-placeholder.png'}
                src={record?.variantImg}
                width={50}
              />
            </div>
            <div className="cart-product-name" style={{ flexGrow: 1 }}>
              <a onClick={() => onProductClick(record.productData)} >{record.productData.product.productName}</a>
              <div className="my-5 color-secondary font-13">
                <strong>{t('label.variation')}: </strong>
                <span><strong>{record.selectedVariant.variantName}&nbsp;</strong></span>
                {record.productType == 'VOUCHER' ? <span className="custom-badge dark-pink">VOUCHER</span> : null}
                {record?.productType === 'NO_SHIPPING' ? 
                <h5 className="items-direct-beneficiary-badge my-10">
                  <RiTruckLine className="truck-icon" size={20}/>
                  <span>{t('label.itemsWillBeSendToBeneficiary')}</span>
                </h5> : null}
              </div>
            </div>

          </div>
        )
      }
    },
    {
      title: t('label.unitPrice'),
      dataIndex: ['selectedVariant'],
      render: (_: any, record: any) => {
        return (
          <div>
            RM {parseFloat(record.selectedVariant.value).toFixed(2)}
          </div>
        )
      }
    },
    {
      title: t('label.quantity'),
      dataIndex: ['quantity'],
      render: (_: any, record: any) => {
        return (
          <div>
            <MinusCircleOutlined onClick={() => { reduceQuantity(record) }} />
              <input
                className='quantity-input'
                value={record.quantity}
                onBlur={e => handleQuantityBlur(record, record.quantity)}
                onChange={e => handleQuantityChange(record, e.target.value)}
              />
            <PlusCircleOutlined onClick={() => { addQuantity(record) }} />
            {/*<div className="my-5 font-13">
              <span><strong>{parseInt(record.selectedVariant?.stockLimit)}  units available</strong></span>
            </div>*/}
          </div>
        )
      }
    },
    {
      title: t('label.totalPrice'),
      dataIndex: ['totalPrice'],
      render: (_: any, record: any) => {
        return (
          <div>
            RM {(parseFloat(record.selectedVariant.value) * parseFloat(record.quantity || record.__lastSetQuantity)).toFixed(2)}
          </div>
        )
      }
    },
    {
      title: "",
      dataIndex: ['action'],
      render: (_: any, record: any) => {
        return (
          <div>
            <a onClick={() => onRemoveItem(record)}>{t('action.remove')}</a>
          </div>
        )
      }
    },
  ]

  const columnsMobile = [
    {
      title: null,
      dataIndex: ['productData', 'selectedVariant'],
      render: (_: any, record: any) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: 5 }}>
            <div>
              <img
                alt={t('alt.logo')}
                // src={record.productData.featuredImage?.image || '/assets/images/product-placeholder.png'}
                src={record?.variantImg}
                width={50}
              />
            </div>
            <div>
              <a onClick={() => onProductClick(record.productData)}>{record.productData.product.productName}</a>
            </div>

            {/*<div>
              <span>Variations:</span>
              <span className="ml-5"><strong>{record.selectedVariant.variantName}</strong></span>
            </div>*/}
            <div className="my-5 color-secondary font-13">
              <strong>{t('label.variation')}: </strong>
              <span><strong>{record.selectedVariant.variantName}</strong></span>
              {record.productType == 'VOUCHER' ? <span className="mt-5"><br /><span className="custom-badge dark-pink">VOUCHER</span></span> : null}
              {record?.productType === 'NO_SHIPPING' ? 
                <h5 className="items-direct-beneficiary-badge my-10">
                  <RiTruckLine className="truck-icon" size={20}/>
                  <span>{t('label.itemsWillBeSendToBeneficiary')}</span>
                </h5> : null}
            </div>

            <div>
              RM {parseFloat(record.selectedVariant.value).toFixed(2)}
            </div>

            <div style={{ display: 'flex' }}>
              <div style={{ display: 'flex', columnGap: 10, alignItems: 'center' }}>
                <MinusCircleOutlined onClick={() => { reduceQuantity(record) }} />
                  <input
                    className='quantity-input'
                    value={record.quantity}
                    onBlur={e => handleQuantityBlur(record, record.quantity)}
                    onChange={e => handleQuantityChange(record, e.target.value)}
                  />
                <PlusCircleOutlined cellSpacing={10} onClick={() => { addQuantity(record) }} />
              </div>


            </div>

            <div>
              RM {(parseFloat(record.selectedVariant.value) * parseFloat(record.quantity || record.__lastSetQuantity)).toFixed(2)}
            </div>

            <div className="mt-10">
              <a onClick={() => onRemoveItem(record)}>
                {t('action.remove')}
              </a>
            </div>
          </div>
        )
      }
    }
  ];

  const CartIcon = <svg viewBox="0 0 1024 1024" version="1.1"
    xmlns="http://www.w3.org/2000/svg" p-id="1934" width="25" height="25">
    <path
      d="M128.012451 927.969911A3 3 10800 1 0 320.006225 927.969911 3 3 10800 1 0 128.012451 927.969911zM767.991699 927.969911A3 3 10800 1 0 959.985474 927.969911 3 3 10800 1 0 767.991699 927.969911zM959.985474 511.983399 959.985474 127.99585 128.012451 127.99585C128.012451 57.310142 70.702309 0 0.016601 0l0 63.997925c35.294856 0 63.997925 28.703069 63.997925 63.997925l48.062442 412.050639C82.797917 563.501728 64.014526 599.53256 64.014526 639.979249c0 70.685708 57.310142 127.99585 127.99585 127.99585l767.975098 0 0-63.997925L192.010376 703.977174c-35.358853 0-63.997925-28.639071-63.997925-63.997925 0-0.223993 0-0.447985 0.031999-0.671978L959.985474 511.983399z"
      p-id="1935" fill="#196AAF"></path>
  </svg>


  const submitUpdateCart = (data) => {
    let obj = {
      cardId: cartList.cartID,
      items: data
    }
    handleUpdateToCart(obj)
  }

  const onRemoveItem = (cart) => {
    let cartListInfo = filter(cartData, cartObj => {
      return cartObj.cartID != cart.cartID;
    })
    setCartData(cartListInfo);

    if (selectedCartItems.length) {
      let selectedItems = filter(selectedCartItems, selectedCartObj => {
        return selectedCartObj.cartID != cart.cartID;
      })
      setSelectedCartItems(selectedItems);
    }

    submitUpdateCart(cartListInfo) // update cart
  }

  const addQuantity = (cart) => {
    if (status.update == 'loading') {
      return;
    }
    let selectedItemQuantity = cart.quantity + 1;

    if (cart?.selectedVariant?.restrictedQtyLimit &&
      selectedItemQuantity > parseInt(cart?.selectedVariant?.restrictedQtyLimit)
    ) {
      toaster('warning', t('error.overRestrictedLimit', { amount: parseInt(cart.selectedVariant?.restrictedQtyLimit) }));
      return;
    }

    if (parseInt(cart.selectedVariant.stockLimit) < selectedItemQuantity) {
      toaster("warning", t('toaster.selectedQuantityError'))
      return;
    }
    let cartListInfo = map(cartData, cartObj => {
      if (cartObj.cartID == cart.cartID) {
        cartObj.quantity = selectedItemQuantity;
      }
      return cartObj;
    })

    setCartData(cartListInfo);

    if (selectedCartItems.length) {
      let selectedItems = map(selectedCartItems, cartObj => {
        if (cartObj.cartID == cart.cartID) {
          cartObj.quantity = selectedItemQuantity;
        }
        return cartObj;
      })
      setSelectedCartItems(selectedItems);
    }

    submitUpdateCart(cartListInfo)
  }

  const reduceQuantity = (cart) => {
    if (status.update == 'loading') {
      return;
    }
    let selectedItemQuantity = cart.quantity - 1;

    // return if try to set to < 1
    if (selectedItemQuantity < 1) {
      return;
    }

    let cartListInfo = map(cartData, cartObj => {
      if (cartObj.cartID == cart.cartID) {
        cartObj.quantity = selectedItemQuantity;
      }
      return cartObj;
    })
    setCartData(cartListInfo);
    if (selectedCartItems.length) {
      let selectedItems = map(selectedCartItems, cartObj => {
        if (cartObj.cartID == cart.cartID) {
          cartObj.quantity = selectedItemQuantity;
        }
        return cartObj;
      })
      setSelectedCartItems(selectedItems);
    }

    submitUpdateCart(cartListInfo)
  }

  const onRemoveAll = () => {
    setCartData([]);
    submitUpdateCart([]) // update cart removing all so passing empty array
  }
  const { profile } = useSession();
  const [weightValidation, setWeightValidation] = useState(false);

  const onCheckout = () => {
    //  localStorage.setItem('checkoutData', JSON.stringify(selectedCartItems))

    let supplierGroup = groupBy(selectedCartItems, cartObj => {
      return cartObj.productData.orgID;
    })

    let shippingCostItems = map(supplierGroup, (data, id) => {
      let weight = 0;
      data.map((item) => {
        weight += (item.weight * parseFloat(item.quantity))
      })
      return {
        supplierID: id,
        state: profile.primaryAddress.state,
        weight: weight
      }
    })

    let totalWeight = 0;
    map(shippingCostItems, (obj) => {
      totalWeight += obj.weight
    })

    if (totalWeight <= 28) {
      let costObj = {
        orders: shippingCostItems
      }
      setAppliedProductVoucher([]);
      setAppliedPlatformVoucher([]);
      setLocalCartState({
        ...localCartState, list: selectedCartItems, shippingCostObj: costObj
      });
      history.push('/checkout')
    } else {
      setWeightValidation(true)
      setTimeout(() => {
        setWeightValidation(false)
      }, 5000);
    }
  }

  const handleSelectAll = (e) => {
    let cartUpdateData = map(cloneDeep(cartData), cart => {
      // do not select if is in vacation
      if (cart.productData?.orgData?.ecomSetting?.vacationMode &&
        cart.productData?.orgData?.ecomSetting?.vacationOrderHandling === "CHECKOUT_NOT_ALLOWED"
      ) {
        return cart
      }
      cart.selected = e.target.checked
      return cart;
    })

    if (e.target.checked == true) {
      // remove unselected cart
      setSelectedCartItems(cartUpdateData.filter(cart => cart.selected));
    } else {
      setSelectedCartItems([]);
    }

    setCartData(cartUpdateData)
    // setInput({ ...input, [name]: value });
  }

  return (
    <div className="product product-cart user-select-none">

      <Row gutter={16}>
        <div className="mx-10" style={{ display: 'flex', gap: '10px' }}>

          <img src='/assets/images/cart-icon.png' className="checkout-icon" width={30} />
          <h3 className="title">{t('pageTitle.shoppingCart')}</h3>

        </div>
        {groupedList.length > 0 && <Col className="my-10" span={24}><Alert
          type="info"
          message={t('txt.cartAutoUpdateMsg')}
          showIcon />
        </Col>}
        <Col span={24}>

          {/* For mobile */}
          <Row>
            <Col xxl={0} xl={0} lg={0} md={24} sm={24} xs={24}>
              {
                groupedList.length ?
                  groupedList.map((o: any, i: any) => {
                    return (
                      <div>
                        <div className="shopping-details my-10 py-1" key={i}>
                          <Col lg={24} md={24} sm={24} xs={24}>
                            <div className="cart-table">
                              <DataTable
                                rowSelection={{
                                  type: 'checkbox',
                                  checked: o.selected,
                                  ...rowSelection,
                                }}
                                rowKey={(record: any) => {
                                  return record.cartID
                                }}
                                columns={columnsMobile}
                                dataSource={o.products}
                                title={() => (
                                  <React.Fragment>
                                    <img
                                      alt={t('alt.logo')}
                                      src={o.supplierData?.image_file?.file_url || '/assets/images/product-placeholder.png'}
                                      width={50}
                                    />
                                    <span className="ml-10 color-primary">{o.supplierData?.name}</span>
                                    {
                                      o.supplierData?.ecomSetting?.vacationMode &&
                                      <VacationInfoBox
                                        infoOption={o.supplierData?.ecomSetting?.vacationOrderHandling}
                                        type={
                                          o.supplierData?.ecomSetting?.vacationOrderHandling === "CHECKOUT_NOT_ALLOWED" ?
                                            "warning" :
                                            "info"
                                        }
                                      />
                                    }
                                  </React.Fragment>
                                )}
                              />
                              <div className="p-10 voucher-text">
                                {t('txt.discountVouchersAvailableMsg')}
                              </div>
                            </div>
                          </Col>
                        </div>
                      </div>
                    )
                  })
                  :
                  <>
                    {status.fetch == 'loading' ?
                      <>
                        <SkeletonLoader type="default" />
                        <SkeletonLoader type="default" />
                        <SkeletonLoader type="default" />
                      </>
                      :
                      <div className="shopping-details my-10">
                        <Col lg={24} md={24} sm={24} xs={24}>
                          <div className="cart-table">
                            <div className="my-10 m-auto justify-center">
                              <div>
                                <img
                                  alt={t('alt.logo')}
                                  src='/assets/images/emptycart.png'
                                  width={300}
                                />
                              </div>
                            </div>
                          </div>
                        </Col>
                      </div>
                    }

                  </>
              }
            </Col>
          </Row>

          {/* For desktop */}
          <Row>

            <Col xxl={24} xl={24} lg={24} md={0} sm={0} xs={0}>
              {groupedList.length ? groupedList.map((o, i) => {
                return (
                  <div>
                    <div className="shopping-details my-10 py-1" key={i}>
                      <Col lg={24} md={24} sm={24} xs={24}>
                        <div className="cart-table">
                          <DataTable
                            rowSelection={{
                              type: 'checkbox',
                              ...rowSelection,
                            }}
                            rowKey={(record: any) => {
                              return record.cartID
                            }}
                            columns={columns}
                            dataSource={o.products}
                            title={() => (
                              <React.Fragment>
                                <img
                                  alt={t('alt.logo')}
                                  src={o.supplierData?.image_file?.file_url || '/assets/images/product-placeholder.png'}
                                  width={50}
                                />

                                <span className="ml-10">{o.supplierData?.name}</span>
                                {
                                  o.supplierData?.ecomSetting?.vacationMode &&
                                  <VacationInfoBox
                                    infoOption={o.supplierData?.ecomSetting?.vacationOrderHandling}
                                    type={
                                      o.supplierData?.ecomSetting?.vacationOrderHandling === "CHECKOUT_NOT_ALLOWED" ?
                                        "warning" :
                                        "info"
                                    }
                                  />
                                }
                              </React.Fragment>
                            )}
                            scroll={{ x: 1000 }}
                          />
                          <div className="p-10 voucher-text">
                            {t('txt.discountVouchersAvailableMsg')}
                          </div>
                        </div>
                      </Col>
                    </div>
                  </div>
                )
              }) :
                <>
                  {status.fetch == 'loading' ?
                    <>
                      <SkeletonLoader type="default" />
                      <SkeletonLoader type="default" />
                      <SkeletonLoader type="default" />
                    </>
                    :
                    <div className="shopping-details my-10">
                      <Col lg={24} md={24} sm={24} xs={24}>
                        <div className="cart-table">
                          <div className="my-10 m-auto justify-center">
                            <div>
                              <img
                                alt={t('alt.logo')}
                                src='/assets/images/emptycart.png'
                                width={300}
                              />
                            </div>
                          </div>
                        </div>
                      </Col>
                    </div>
                  }

                </>}
            </Col>
            <Col lg={24} md={24} sm={24} xs={24} style={{ marginBottom: '15px', marginTop: '10px' }}>
              {weightValidation && <Alert message={t('txt.WeightValidation')} type="warning" showIcon />}
            </Col>
          </Row>
          {groupedList.length !== 0 &&
            <div className="shopping-total-items fixed-sticky">

              <Col lg={24} md={24} sm={24} xs={24}>
                <div className="total-items">
                  <Row gutter={16} align='middle'>
                    <Col lg={12} md={12} sm={24} xs={24}>
                      <div className='total-price'>

                        <div>
                          <Checkbox
                            onChange={handleSelectAll}
                          >
                          </Checkbox>
                        </div>
                        <div>
                          <span className="txt-white">{t('label.selectAll')}</span>
                        </div>

                        <div style={{ 'cursor': 'pointer' }} onClick={() => onRemoveAll()}>
                          <span>{t('action.removeAll')}</span>
                        </div>

                      </div>
                    </Col>

                    <Col lg={12} md={12} sm={24} xs={24}>
                      <div className='total-price' style={{ justifyContent: 'flex-end' }}>
                        <div style={{ alignSelf: 'center' }}>
                          <span>{t('txt.total')} ({selectedCartItems.length} {selectedCartItems.length < 2 ? t('txt.item') : t('txt.items')}) :</span>
                        </div>
                        <div className="total-price--amount-wrapper">
                          <span style={{ fontSize: '24px' }}>RM{parseFloat(input.totalAmount).toFixed(2)}</span>
                        </div>
                        <div style={{ padding: '5px 0px' }}>
                          <Button
                            isLoading={status === "loading"}
                            className="btn-checkout"
                            type="secondary"
                            isDisabled={selectedCartItems.length == 0}
                            onClick={() => onCheckout()}
                            label={
                              <React.Fragment>
                                <span>{t('action.checkOut')}</span> <ArrowRightOutlined />
                              </React.Fragment>
                            }
                            size="middle"
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </div>
          }
        </Col>
      </Row>

      {/* After Refund is confirm, then uncomment back */}
      {/* <Alert
        className="mt-10"
        type="info"
        message={(
          <div className="text-blue-300" dangerouslySetInnerHTML={{ __html: t('txt.cancelOrderConfirmationDescTwo') }}/>
        )}
      /> */}
    </div>
  )
}


export default ProductCart;
